import { c as _c } from "react/compiler-runtime";
import { CLICK_ID_STORAGE_KEYS, type PersistedClickIdKey, toClickIdFallbacks } from './click-id-fallbacks';
import { usePersistedCookieParam, usePersistedQueryParam } from './use-persisted-param';
import { usePersistedReferrerParams } from './use-persisted-referrer-params';
const UTM_STORAGE_KEYS = {
  source: '__utm_source',
  campaign: '__utm_campaign',
  medium: '__utm_medium',
  content: '__utm_content',
  partner: '__partner',
  portfolioCode: '__portfolio_code'
} as const;
interface UtmParamValues {
  campaign: string;
  content: string;
  fbclid: string;
  gbraid: string;
  gclid: string;
  medium: string;
  msclkid: string;
  partner: string;
  portfolioCode: string;
  portfolioUrl: string | null;
  referrer: string;
  source: string;
  ttclid: string;
  ttp: string;
  wbraid: string;
}
interface UtmParamSetters {
  setCampaign: (value: string) => void;
  setContent: (value: string) => void;
  setFbclid: (value: string) => void;
  setGbraid: (value: string) => void;
  setGclid: (value: string) => void;
  setMedium: (value: string) => void;
  setMsclkid: (value: string) => void;
  setPartner: (value: string) => void;
  setPortfolioCode: (value: string) => void;
  setPortfolioUrl: (value: string) => void;
  setReferrer: (value: string) => void;
  setSource: (value: string) => void;
  setTtclid: (value: string) => void;
  setTtp: (value: string) => void;
  setWbraid: (value: string) => void;
}
export interface UtmParams extends UtmParamValues, UtmParamSetters {
  clickIdFallbacks: Record<PersistedClickIdKey, string>;
}

/**
 * URL + localStorage attribution params. URL wins; stored values survive
 * in-app navigation and OAuth redirects that strip the query string.
 */
export function useUtmParams() {
  const $ = _c(41);
  const {
    referrer,
    portfolioUrl,
    setReferrer,
    setPortfolioUrl
  } = usePersistedReferrerParams();
  const {
    value: source,
    setValue: setSource
  } = usePersistedQueryParam("utm_source", UTM_STORAGE_KEYS.source);
  const {
    value: campaign,
    setValue: setCampaign
  } = usePersistedQueryParam("utm_campaign", UTM_STORAGE_KEYS.campaign);
  const {
    value: medium,
    setValue: setMedium
  } = usePersistedQueryParam("utm_medium", UTM_STORAGE_KEYS.medium);
  const {
    value: content,
    setValue: setContent
  } = usePersistedQueryParam("utm_content", UTM_STORAGE_KEYS.content);
  const {
    value: partner,
    setValue: setPartner
  } = usePersistedQueryParam("partner", UTM_STORAGE_KEYS.partner);
  const {
    value: portfolioCode,
    setValue: setPortfolioCode
  } = usePersistedQueryParam("portfolio_code", UTM_STORAGE_KEYS.portfolioCode);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      cookieFallback: "ttclid"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    value: ttclid,
    setValue: setTtclid
  } = usePersistedQueryParam("ttclid", CLICK_ID_STORAGE_KEYS.ttclid, t0);
  const {
    value: gclid,
    setValue: setGclid
  } = usePersistedQueryParam("gclid", CLICK_ID_STORAGE_KEYS.gclid);
  const {
    value: fbclid,
    setValue: setFbclid
  } = usePersistedQueryParam("fbclid", CLICK_ID_STORAGE_KEYS.fbclid);
  const {
    value: msclkid,
    setValue: setMsclkid
  } = usePersistedQueryParam("msclkid", CLICK_ID_STORAGE_KEYS.msclkid);
  const {
    value: gbraid,
    setValue: setGbraid
  } = usePersistedQueryParam("gbraid", CLICK_ID_STORAGE_KEYS.gbraid);
  const {
    value: wbraid,
    setValue: setWbraid
  } = usePersistedQueryParam("wbraid", CLICK_ID_STORAGE_KEYS.wbraid);
  const {
    value: ttp,
    setValue: setTtp
  } = usePersistedCookieParam("_ttp", CLICK_ID_STORAGE_KEYS.ttp);
  let t1;
  if ($[1] !== fbclid || $[2] !== gbraid || $[3] !== gclid || $[4] !== msclkid || $[5] !== ttclid || $[6] !== ttp || $[7] !== wbraid) {
    t1 = toClickIdFallbacks({
      ttclid,
      gclid,
      fbclid,
      msclkid,
      ttp,
      gbraid,
      wbraid
    });
    $[1] = fbclid;
    $[2] = gbraid;
    $[3] = gclid;
    $[4] = msclkid;
    $[5] = ttclid;
    $[6] = ttp;
    $[7] = wbraid;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== campaign || $[10] !== content || $[11] !== fbclid || $[12] !== gbraid || $[13] !== gclid || $[14] !== medium || $[15] !== msclkid || $[16] !== partner || $[17] !== portfolioCode || $[18] !== portfolioUrl || $[19] !== referrer || $[20] !== setCampaign || $[21] !== setContent || $[22] !== setFbclid || $[23] !== setGbraid || $[24] !== setGclid || $[25] !== setMedium || $[26] !== setMsclkid || $[27] !== setPartner || $[28] !== setPortfolioCode || $[29] !== setPortfolioUrl || $[30] !== setReferrer || $[31] !== setSource || $[32] !== setTtclid || $[33] !== setTtp || $[34] !== setWbraid || $[35] !== source || $[36] !== t1 || $[37] !== ttclid || $[38] !== ttp || $[39] !== wbraid) {
    t2 = {
      referrer,
      portfolioUrl,
      source,
      campaign,
      medium,
      content,
      partner,
      portfolioCode,
      ttclid,
      gclid,
      fbclid,
      msclkid,
      ttp,
      gbraid,
      wbraid,
      clickIdFallbacks: t1,
      setReferrer,
      setSource,
      setCampaign,
      setMedium,
      setContent,
      setPartner,
      setPortfolioCode,
      setPortfolioUrl,
      setTtclid,
      setGclid,
      setFbclid,
      setMsclkid,
      setTtp,
      setGbraid,
      setWbraid
    };
    $[9] = campaign;
    $[10] = content;
    $[11] = fbclid;
    $[12] = gbraid;
    $[13] = gclid;
    $[14] = medium;
    $[15] = msclkid;
    $[16] = partner;
    $[17] = portfolioCode;
    $[18] = portfolioUrl;
    $[19] = referrer;
    $[20] = setCampaign;
    $[21] = setContent;
    $[22] = setFbclid;
    $[23] = setGbraid;
    $[24] = setGclid;
    $[25] = setMedium;
    $[26] = setMsclkid;
    $[27] = setPartner;
    $[28] = setPortfolioCode;
    $[29] = setPortfolioUrl;
    $[30] = setReferrer;
    $[31] = setSource;
    $[32] = setTtclid;
    $[33] = setTtp;
    $[34] = setWbraid;
    $[35] = source;
    $[36] = t1;
    $[37] = ttclid;
    $[38] = ttp;
    $[39] = wbraid;
    $[40] = t2;
  } else {
    t2 = $[40];
  }
  return t2;
}